import { MapViewerConfiguration } from "../../config/map-viewer-configuration";
import { DataTransporter } from "../data-transporter";
import { computed, makeObservable, observable } from "mobx";
import { Nullable, Optional } from "../../globals/types";
import _ from "lodash";
import { DataNotFound } from "../../globals/errors";

export class ContactStore {
    mapViewerConfig: MapViewerConfiguration;
    dataTransporter: DataTransporter;
    contacts: Contact[];

    constructor(mapViewerConfig: MapViewerConfiguration, dataTransporter: DataTransporter) {
        this.mapViewerConfig = mapViewerConfig;
        this.dataTransporter = dataTransporter;
        this.contacts = [];
    }

    async loadAll(): Promise<Contact[]> {
        this.contacts = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/contacts`);
        const contactDtos: ContactDto[] = result.data;
        contactDtos.forEach(c => this.contacts.push(new Contact(c)))
        return this.contacts;
    }

    async load(id: number): Promise<Contact> {
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/contacts/${id}`);
        const contactDto: ContactDto = result.data;
        const contact = new Contact(contactDto);
        this.contacts.push(contact);
        return contact;
    }

    async add(contactDto: ContactDto): Promise<Contact> {
        const result = await this.dataTransporter.post(`${this.mapViewerConfig.api_base_url}/contacts`, contactDto);
        const contactDtoResult: ContactDto = result.data;
        const contact = new Contact(contactDtoResult);
        this.contacts.push(contact);
        return contact;
    }

    async update(contactDto: ContactDto): Promise<Contact> {
        const contact = this.contacts.find(c => c.id == contactDto.id);

        if (contact !== undefined) {
            const result = await this.dataTransporter.put(`${this.mapViewerConfig.api_base_url}/contacts/${contactDto.id}`, contactDto);
            contact.contactDto = result.data;
            return contact;
        }

        throw new DataNotFound("Contact not found");
    }

    async delete(id: number): Promise<Contact> {
        await this.dataTransporter.delete(`${this.mapViewerConfig.api_base_url}/contacts/${id}`);
        const removedContacts: Contact[] = _.remove(this.contacts, c => c.id === id);
        return removedContacts[0];
    }
}

export class Contact {
    contactDto: ContactDto;

    constructor(contactDto: ContactDto) {
        this.contactDto = contactDto;
        makeObservable(this, {
            contactDto: observable,
            id: computed,
            firstName: computed,
            lastName: computed,
            fullName: computed,
            street: computed,
            streetNr: computed,
            additionalAddressLine: computed,
            zipCode: computed,
            city: computed,
            email: computed,
            phone: computed,
            body: computed,
        })
    }

    get id(): number {
        return this.contactDto.id;
    }

    get firstName(): string {
        return this.contactDto.firstName;
    }

    get lastName(): string {
        return this.contactDto.lastName;
    }

    get fullName(): string {
        return this.firstName + " " + this.lastName;
    }

    get street(): Optional<string> {
        return this.contactDto.street ?? undefined;
    }

    get streetNr(): Optional<string> {
        return this.contactDto.streetNr ?? undefined;
    }

    get additionalAddressLine(): Optional<string> {
        return this.contactDto.additionalAddressLine ?? undefined;
    }

    get zipCode(): Optional<number> {
        return this.contactDto.zipCode ?? undefined;
    }

    get city(): Optional<string> {
        return this.contactDto.city ?? undefined;
    }

    get email(): Optional<string> {
        return this.contactDto.email ?? undefined;
    }

    get phone(): Optional<string> {
        return this.contactDto.phone ?? undefined;
    }

    get body(): Optional<string> {
        return this.contactDto.body ?? undefined;
    }
}

export type ContactDto = {
    id: number;
    firstName: string;
    lastName: string;
    street: Nullable<string>;
    streetNr: Nullable<string>;
    additionalAddressLine: Nullable<string>;
    zipCode: Nullable<number>;
    city: Nullable<string>;
    email: Nullable<string>;
    phone: Nullable<string>;
    body: Nullable<string>;
};
